import { yupResolver } from '@hookform/resolvers/yup'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormLabel,
  Grid,
  InputAdornment,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  Link,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useRecoilState, useSetRecoilState } from 'recoil'
import * as yup from 'yup'
import useSnackbar from '../../data/use-snackbar'
import { getErrorMessage } from '../../error-helper'
import simService from '../../services/sim.service'
import ibuyService from '../../services/ibuy.service'
import {
  activeStepState,
  formDataState,
  loadingState,
  isGlobalDomain,
} from '../helpers'
import Layout from './layout.component'
import BarcodeScanner from './barcodescanner.component'
import { useLocation, useNavigate } from 'react-router-dom'
import AppConfig from '../../config'

const PREFIX = 'ScanSim'

const constraints = {
  width: { min: 1280, ideal: 1920, max: 3840 },
  height: { min: 480, ideal: 1080, max: 1080 },
  facingMode: { ideal: 'environment' },
  aspectRatio: '1',
  focusMode: 'continuous',
}

export const classes = {
  error: `${PREFIX}-error`,
  input: `${PREFIX}-input`,
  label: `${PREFIX}-label`,
  image: `${PREFIX}-image`,
  containerButton: `${PREFIX}-container-button`,
  simScannerContainer: `${PREFIX}-sim-scanner-container`,
  redemptionBtn: `${PREFIX}-redemption-btn`,
}

export const styles = ({ theme }) => ({
  [`& .${classes.error}`]: {
    color: '#e74c3c',
    fontWeight: '600',
  },
  [`& .${classes.input} > input`]: {
    padding: theme.spacing(0.6, 1),
    fontSize: theme.spacing(1.75),
  },
  [`& .${classes.label}`]: {
    fontSize: theme.spacing(1.5),
    color: '#505050',
    fontWeight: '600',
  },
  [`& .${classes.label} .MuiFormLabel-asterisk`]: {
    color: theme.palette.primary.main,
  },
  [`& .${classes.image}`]: {
    width: '100%',
    height: 243,
    background: "url('./scan.png')",
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% auto',
  },
  [`& .${classes.redemptionBtn}`]: {
    marginTop: '0.5rem',
  },
  [theme.breakpoints.down('md')]: {
    [`& .${classes.containerButton}`]: {
      padding: theme.spacing(0, 3),
    },
  },
})

const StyledDiv = styled('div')(styles)

function ScanSim({ step, handleBack, enabledRedeem = false }) {
  const setActiveStep = useSetRecoilState(activeStepState)
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useRecoilState(formDataState)
  const [scanMsisdn, setScanMsisdn] = useState('')
  const [scanIccid, setScanIccid] = useState('')
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const setLoading = useSetRecoilState(loadingState)
  const location = useLocation()
  const navigate = useNavigate()
  const [redeemState, setRedeemState] = useState(null)
  const [isDataOnlyPlan, setIsDataOnlyPlan] = useState(false)
  const isRedeem = useMemo(() => {
    if (redeemState && redeemState.isValid) {
      return true
    }

    return false
  }, [redeemState])
  const isEmailRequired = useMemo(() => {
    if (redeemState && redeemState.isValid === true) {
      return true
    }

    if (isDataOnlyPlan) {
      return true
    }

    return false
  }, [redeemState, isDataOnlyPlan])

  const handleScan = async () => {
    setScanMsisdn('')
    setScanIccid('')
    setOpen(true)
  }

  const handleCapture = async () => {
    if (scanMsisdn) {
      setValue('msisdn', scanMsisdn, { shouldDirty: true })
    }
    if (scanIccid) {
      setValue('iccid', scanIccid, { shouldDirty: true })
    }

    trigger()
    setOpen(false)
  }

  const handleUpdate = (result) => {
    if (result) {
      console.log('result', result)
      if (result.length === 8) {
        setScanMsisdn(result)
      } else if (result.length === 18) {
        setScanIccid(result)
      } else if (result.length === 26) {
        let tmp_msisdn = result.substring(0, 8)
        let tmp_iccid = result.substring(8)
        setScanMsisdn(tmp_msisdn)
        setScanIccid(tmp_iccid)
      }
    }
  }

  const handleClose = (event, reason) => {
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      // ignore
      return
    }

    setOpen(false)
  }

  const validationSchema = yup.object().shape({
    msisdn: yup
      .string()
      .required(
        "We can't detect your MSISDN barcode, you can either try to scan it again or manually key it in."
      )
      .matches(/^[0-9]{8}$/, 'Invalid format'),
    iccid: yup
      .string()
      .required(
        "We can't detect your ICCID barcode, you can either try to scan it again or manually key it in."
      )
      .matches(/^[0-9]{18}$/, 'Invalid format'),
    emailRequired: yup.boolean(),
    email: yup
      .string()
      .nullable()
      .email("Email should be in the format 'example@domain.com'.")
      .test('email-required', 'Email is required', function (value) {
        const { emailRequired } = this.parent
        if (emailRequired) {
          return value ? true : false
        }

        return true
      }),
    emailConfirm: yup
      .string()
      .nullable()
      .email("Email should be in the format 'example@domain.com'.")
      .test('email-required', 'Email is required', function (value) {
        const { emailRequired } = this.parent
        if (emailRequired) {
          return value ? true : false
        }

        return true
      })
      .test(
        'email-not-match',
        'Your email addresses do not match. Please check and try again.',
        function (value) {
          const { emailRequired, email } = this.parent
          if (emailRequired) {
            return value === email ? true : false
          }

          return true
        }
      ),
  })

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      msisdn: formData.msisdn || '',
      iccid: formData.iccid || '',
      redemptionCode: formData.redemptionCode || '',
      emailRequired: false,
      email: formData.email || '',
      emailConfirm: formData.emailConfirm || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (data) => {
    let result,
      valid = false,
      errMessage = 'Invalid card or status'

    try {
      setLoading(true)
      const request = {
        msisdn: data.msisdn,
        iccid: data.iccid,
        imsi: data.imsi,
        status: 'PREALLOC',
        subType: formData.subType,
        idType: formData.idType,
        isRedeem: isRedeem,
        isGlobalDomain: isGlobalDomain,
      }

      result = await simService.check(request)

      valid = result.code <= 2000
      errMessage = result.message
    } catch (error) {
      errMessage = getErrorMessage(error)
    } finally {
      setLoading(false)
    }

    if (!valid) {
      if (result?.code === 2011) {
        return enqueueSnackbar(
          <>
            This SIM can only be registered in Singapore.{' '}
            <Link
              href={AppConfig.SELFREG_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here
            </Link>{' '}
            when you arrive in Singapore to register your SIM.
          </>
        )
      }

      enqueueSnackbar(errMessage)
      return
    }

    if (result.isDataOnlyPlan && !isDataOnlyPlan) {
      setIsDataOnlyPlan(true)
      setValue('emailRequired', true)
      trigger(['email', 'emailConfirm'])
      return
    }

    setFormData({
      ...formData,
      ...data,
      biic: '',
      biis: '',
      isDataOnlyPlan: result.isDataOnlyPlan,
      redemptionCode: data.redemptionCode,
    })

    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleRemove = async () => {
    try {
      setLoading(true)
      await ibuyService.cancelRedeem(getValues('redemptionCode').trim())
      setRedeemState(null)
      setIsDataOnlyPlan(false)
      setFormData({
        redemptionCode: '',
        msisdn: '',
        iccid: '',
        email: '',
        emailConfirm: '',
      })
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleApply = async () => {
    const code = getValues('redemptionCode')
    if (!code) {
      enqueueSnackbar('Redemption code is required.')
      return false
    }

    await validateCode(code.trim())
  }

  const resetField = useCallback(() => {
    setValue('redemptionCode', '')
    setValue('msisdn', '')
    setValue('iccid', '')
    setValue('emailRequired', false)
    setValue('email', '')
    setValue('emailConfirm', '')
  }, [setValue])

  const handleReset = () => {
    setIsDataOnlyPlan(false)
    resetField()
    trigger()
  }

  const validateCode = useCallback(
    async (code, skipAlert = false) => {
      setLoading(true)
      try {
        const result = await ibuyService.validateRedemptionCode(code)
        if (result && !result.isValid) {
          enqueueSnackbar('Invalid redemption code or status.')
          resetField()
          return false
        }

        if (!skipAlert) {
          enqueueSnackbar(
            'Your redemption is successful. Please input your email address and select “Confirm” to continue with your registration.',
            {
              variant: 'success',
            }
          )
        }

        setRedeemState(result)
      } catch (err) {
        enqueueSnackbar(getErrorMessage(err))
        return false
      } finally {
        setLoading(false)
      }
    },
    [enqueueSnackbar, setLoading, resetField]
  )

  useEffect(() => {
    if (redeemState && redeemState.isValid) {
      setValue('msisdn', redeemState.sim.msisdn, { shouldDirty: true })
      setValue('iccid', redeemState.sim.iccid, { shouldDirty: true })
      trigger()
      setValue('emailRequired', true)
    }

    if (!redeemState && formData.redemptionCode === '') {
      resetField()
    }
  }, [redeemState, setValue, trigger, resetField, formData.redemptionCode])

  useEffect(() => {
    if (enabledRedeem) {
      const queryParams = new URLSearchParams(location.search)
      let codeValue = queryParams.get('rcode')
      let skipAlert = false
      if (!codeValue && getValues('redemptionCode')) {
        codeValue = getValues('redemptionCode')
        skipAlert = true
      }

      if (codeValue) {
        validateCode(codeValue, skipAlert)
        navigate(location.pathname, { replace: true })
      }
    }
  }, [
    location.search,
    validateCode,
    location.pathname,
    navigate,
    enabledRedeem,
    getValues,
  ])

  return (
    <>
      <Layout
        step={step}
        content={
          <StyledDiv>
            <Grid container columnSpacing={8}>
              <Grid item xs={12} md={6} lg={5}>
                <Typography variant="subtitle2" sx={{ mb: '20px' }}>
                  Please scan the barcode located on your M1 SIM card.
                </Typography>
                <div className={classes.image} />
              </Grid>
              <Grid item xs={12} md={6}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  {enabledRedeem && (
                    <>
                      <FormLabel className={classes.label}>
                        Redemption Code (optional)
                      </FormLabel>
                      <Controller
                        control={control}
                        name="redemptionCode"
                        defaultValue={formData.redemptionCode || ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            placeholder="Redemption Code"
                            name="redemptionCode"
                            id="redemptionCode"
                            disabled={isRedeem || isDataOnlyPlan}
                            error={errors.redemptionCode ? true : false}
                            helperText={errors.redemptionCode?.message}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {isRedeem ? (
                                    <Button
                                      className={classes.redemptionBtn}
                                      variant="contained"
                                      color="primary"
                                      fullWidth={isMobile}
                                      onClick={handleRemove}
                                    >
                                      {' '}
                                      Remove{' '}
                                    </Button>
                                  ) : (
                                    <Button
                                      className={classes.redemptionBtn}
                                      variant="contained"
                                      color="primary"
                                      fullWidth={isMobile}
                                      disabled={isDataOnlyPlan}
                                      onClick={handleApply}
                                    >
                                      {' '}
                                      Apply{' '}
                                    </Button>
                                  )}
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </>
                  )}
                  <FormLabel className={classes.label} required>
                    MSISDN / Your new M1 mobile number
                  </FormLabel>
                  <Controller
                    control={control}
                    name="msisdn"
                    defaultValue={formData.msisdn || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        placeholder="MSISDN No."
                        name="msisdn"
                        id="msisdn"
                        disabled={isRedeem || isDataOnlyPlan}
                        error={errors.msisdn ? true : false}
                        helperText={errors.msisdn?.message}
                        InputProps={
                          isDataOnlyPlan
                            ? {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Button
                                      className={classes.redemptionBtn}
                                      variant="contained"
                                      color="primary"
                                      fullWidth={isMobile}
                                      onClick={handleReset}
                                    >
                                      {' '}
                                      Reset{' '}
                                    </Button>
                                  </InputAdornment>
                                ),
                              }
                            : undefined
                        }
                      />
                    )}
                  />
                  <FormLabel
                    className={classes.label}
                    required
                    asterisk={{ color: 'red' }}
                  >
                    ICCID number / SIM Card serial number
                  </FormLabel>
                  <Controller
                    control={control}
                    name="iccid"
                    defaultValue={formData.iccid || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        placeholder="ICCID No."
                        name="iccid"
                        id="iccid"
                        disabled={isRedeem || isDataOnlyPlan}
                        error={errors.iccid ? true : false}
                        helperText={errors.iccid?.message}
                      />
                    )}
                  />
                  {isEmailRequired ? (
                    <>
                      <FormLabel className={classes.label} required>
                        Email Address
                      </FormLabel>
                      <>
                        <Typography variant="subtitle3">
                          Please ensure the email address provided is correct to
                          receive your eSIM QR code
                        </Typography>
                      </>
                      <Controller
                        control={control}
                        name="email"
                        defaultValue={formData.email || ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            placeholder="Email Address."
                            name="email"
                            id="email"
                            error={errors.email ? true : false}
                            helperText={errors.email?.message}
                          />
                        )}
                      />
                      <FormLabel className={classes.label} required>
                        Confirm Email Address
                      </FormLabel>
                      <Controller
                        control={control}
                        name="emailConfirm"
                        defaultValue={formData.email || ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            placeholder="Confirm Email Address."
                            name="emailConfirm"
                            id="emailConfirm"
                            error={errors.emailConfirm ? true : false}
                            helperText={errors.emailConfirm?.message}
                          />
                        )}
                      />
                    </>
                  ) : null}

                  <Button
                    endIcon={<CameraAltOutlinedIcon />}
                    variant="contained"
                    color="primary"
                    fullWidth={isMobile}
                    disabled={isRedeem || isDataOnlyPlan}
                    onClick={handleScan}
                  >
                    Scan
                  </Button>
                  <Typography variant="subtitle2">
                    Please ensure the information displayed above is the same as
                    what is printed on your SIM card package.
                    <br />
                    <br />
                    If the details are incorrect, please click{' '}
                    <strong>&quot;Scan&quot;</strong> to resubmit your
                    information.
                  </Typography>
                  <Grid
                    container
                    columnSpacing={1}
                    direction={{ xs: 'column-reverse', md: 'row' }}
                    className={classes.containerButton}
                  >
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={handleBack}
                      >
                        Back
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!isValid}
                        type="submit"
                      >
                        Confirm
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Grid>
            </Grid>
          </StyledDiv>
        }
      />
      <Dialog
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        open={open}
        onClose={handleClose}
        className={classes.simScannerContainer}
      >
        <DialogContent sx={{ overflow: 'hidden' }}>
          <BarcodeScanner
            constraints={constraints}
            isStart={open}
            setIdScanUpdate={handleUpdate}
            decoder={['code_128_reader']}
          />
          <Typography>Scanning...</Typography>
          <Typography>MSISDN: {scanMsisdn}</Typography>
          <Typography>ICCID: {scanIccid}</Typography>
        </DialogContent>
        <DialogActions>
          {(scanMsisdn || scanIccid) && (
            <Button variant="contained" onClick={handleCapture} color="primary">
              {!scanMsisdn || !scanIccid ? 'Manual Input' : 'Done'}
            </Button>
          )}
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

ScanSim.propTypes = {
  step: PropTypes.object.isRequired,
  handleBack: PropTypes.func.isRequired,
  enabledRedeem: PropTypes.bool,
}

export default ScanSim
